/**
 * Рендер Markdown + LaTeX у React-елементи.
 *
 * Безпека: ніколи не збираємо HTML із користувацького вводу й не кладемо його в
 * dangerouslySetInnerHTML. Натомість робимо власний міні-парсер, який повертає
 * React-ноди. Єдиний виняток — власний вивід KaTeX.renderToString(), який ми
 * генеруємо самі з екранованого тексту (trust: false).
 *
 * Підтримується:
 *   — $...$      інлайн-математика
 *   — $$...$$    окремий блок математики
 *   — \(...\)    інлайн (сумісність із ChatGPT/іншими LLM)
 *   — \[...\]    блок
 *   — **жирний**, *курсив*, `код`
 *   — # Заголовки, ## Підзаголовки, ### Дрібні
 *   — - маркований список та 1. нумерований
 *   — > цитата
 *   --- горизонтальна лінія
 *   | таблиці | (GFM, потрібні для формул у рядках)
 *   ![alt](url) зображення
 *   [текст](url) посилання
 *   ~~закреслений~~
 */

import { Fragment, createElement } from 'react'
import katex from 'katex'
import { COMBINING_ACUTE } from './stress.js'

// ─────────────────────────────────────────────────────────────────────────────
//  LaTeX
// ─────────────────────────────────────────────────────────────────────────────

const KATEX_OPTS = {
  throwOnError: false,
  errorColor: '#ef4444',
  strict: false,
  trust: false,
  output: 'html',
  macros: {
    '\\ctg': '\\operatorname{ctg}',
    '\\arctg': '\\operatorname{arctg}',
    '\\tg': '\\operatorname{tg}',
  },
}

const escapeHtml = (s) =>
  s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')

/** KaTeX у вигляді React-нод. Помилки LaTeX підсвічуються червоним, але не ламають сторінку. */
function renderLatex(tex, displayMode) {
  try {
    const html = katex.renderToString(tex, { ...KATEX_OPTS, displayMode })
    return createElement('span', {
      className: displayMode ? 'katex-block' : 'katex-inline',
      dangerouslySetInnerHTML: { __html: html },
    })
  } catch {
    return createElement(
      'code',
      { className: 'rounded bg-rose-50 px-1.5 py-0.5 text-rose-700 dark:bg-rose-950/40' },
      tex,
    )
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Токенізація: спершу ділимо на блоки, потім на інлайн-частини
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Знаходить інлайн-математику поза вже обробленими ділянками.
 * Повертає масив шмамків: { type: 'text' | 'math', value, display }
 */
function tokenizeInline(text) {
  const out = []
  let buf = ''
  let i = 0

  const flush = () => {
    if (buf) out.push({ type: 'text', value: buf })
    buf = ''
  }

  while (i < text.length) {
    const ch = text[i]

    // $$...$$ — окремий блок (у контексті інлайну теж підтримуємо)
    if (ch === '$' && text[i + 1] === '$') {
      const end = text.indexOf('$$', i + 2)
      if (end > -1) {
        flush()
        out.push({ type: 'math', value: text.slice(i + 2, end).trim(), display: true })
        i = end + 2
        continue
      }
    }

    // \(...\)
    if (ch === '\\' && text[i + 1] === '(') {
      const end = text.indexOf('\\)', i + 2)
      if (end > -1) {
        flush()
        out.push({ type: 'math', value: text.slice(i + 2, end).trim(), display: false })
        i = end + 2
        continue
      }
    }

    // $...$ — інлайн. Не захоплюємо «$5 і $10» (цифра перед $)
    if (ch === '$' && text[i + 1] !== ' ') {
      const end = findInlineMathEnd(text, i + 1)
      if (end > -1) {
        flush()
        out.push({ type: 'math', value: text.slice(i + 1, end).trim(), display: false })
        i = end + 1
        continue
      }
    }

    buf += ch
    i++
  }

  flush()
  return out
}

/**
 * Шукає закриваюче `$`, не заходячи в сусідні слова-«ціни».
 * Правило: math закінчується на `$`, за яким не стоїть цифра (щоб не з'їсти «$5»).
 */
function findInlineMathEnd(text, from) {
  for (let i = from; i < text.length; i++) {
    if (text[i] === '\\') {
      i++
      continue
    }
    if (text[i] !== '$') continue
    // '$' у кінці речення або перед пробілом/розділювачем — це кінець формули
    const next = text[i + 1]
    if (next === undefined || next === ' ' || /[.,;:!?)\]}\n]/.test(next)) return i
  }
  return -1
}

// ─────────────────────────────────────────────────────────────────────────────
//  Markdown у рядку
// ─────────────────────────────────────────────────────────────────────────────

const INLINE_RULES = [
  // !\[alt\](url) — зображення
  {
    re: /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/,
    render: (m, key) =>
      createElement('img', {
        key,
        src: safeUrl(m[2]),
        alt: m[1],
        loading: 'lazy',
        className: 'my-2 max-h-72 rounded-lg border border-slate-200 dark:border-slate-700',
      }),
  },
  // [текст](url)
  {
    re: /^\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/,
    render: (m, key) =>
      createElement('a', {
        key,
        href: safeUrl(m[2]),
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
        className: 'text-sky-600 underline underline-offset-2 hover:text-sky-500 dark:text-sky-400',
      }, m[1]),
  },
  // ~~закреслений~~
  { re: /^~~([^~]+)~~/, render: (m, key) => createElement('del', { key, className: 'opacity-70' }, m[1]) },
  // **жирний**
  { re: /^\*\*([^*]+)\*\*/, render: (m, key) => createElement('strong', { key, className: 'font-semibold' }, m[1]) },
  // __жирний__
  { re: /^__([^_]+)__/, render: (m, key) => createElement('strong', { key, className: 'font-semibold' }, m[1]) },
  // *курсив* / _курсив_
  { re: /^\*([^*\s][^*]*)\*/, render: (m, key) => createElement('em', { key, className: 'italic' }, m[1]) },
  { re: /^_([^_\s][^_]*)_/, render: (m, key) => createElement('em', { key, className: 'italic' }, m[1]) },
  // `код`
  {
    re: /^`([^`]+)`/,
    render: (m, key) =>
      createElement('code', {
        key,
        className: 'rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.9em] text-pink-600 dark:bg-slate-800 dark:text-pink-400',
      }, m[1]),
  },
]

/**
 * Дозволяємо лише безпечні схеми — інакше з картки можна було б вставити
 * javascript: URL і виконати довільний код від імені користувача.
 */
export function safeUrl(url) {
  const u = String(url ?? '').trim()
  if (/^(https?:|data:image\/(png|jpe?g|webp|gif);base64,|blob:)/i.test(u)) return u
  if (u.startsWith('/')) return u
  return '#'
}

/** Розбирає один рядок на React-ноди (Markdown-інлайн + LaTeX). */
function renderInlineText(text, keyPrefix = 'i') {
  const nodes = []
  let key = 0
  let buf = ''

  const flush = () => {
    if (!buf) return
    nodes.push(buf)
    buf = ''
  }

  for (const part of tokenizeInline(text)) {
    if (part.type === 'math') {
      flush()
      nodes.push(renderLatex(part.value, part.display))
      continue
    }

    // У текстовому шматку шукаємо Markdown-конструкції
    let rest = part.value
    while (rest.length) {
      const rule = INLINE_RULES.find((r) => r.re.test(rest))
      if (!rule) {
        const nextSpecial = rest.search(/[![\]*_`~]/)
        if (nextSpecial === -1) {
          buf += rest
          break
        }
        if (nextSpecial > 0) buf += rest.slice(0, nextSpecial)
        rest = rest.slice(nextSpecial)
        continue
      }

      const m = rule.re.exec(rest)
      flush()
      nodes.push(rule.render(m, `${keyPrefix}-${key++}`))
      rest = rest.slice(m[0].length)
    }
  }

  flush()
  return nodes.length ? nodes : ['']
}

// ─────────────────────────────────────────────────────────────────────────────
//  Блочний Markdown
// ─────────────────────────────────────────────────────────────────────────────

const isTableSeparator = (line) => /^\s*\|?[\s:-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes('-')

function splitRow(line) {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim())
}

function renderTable(rows, key) {
  const [head, , ...body] = rows
  return createElement(
    'div',
    { key, className: 'my-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700' },
    createElement(
      'table',
      { className: 'w-full border-collapse text-left text-sm' },
      createElement(
        'thead',
        { className: 'bg-slate-50 dark:bg-slate-800/60' },
        createElement(
          'tr',
          null,
          head.map((cell, i) =>
            createElement(
              'th',
              { key: i, className: 'border-b border-slate-200 px-3 py-2 font-semibold dark:border-slate-700' },
              renderInlineText(cell, `th-${i}`),
            ),
          ),
        ),
      ),
      createElement(
        'tbody',
        null,
        body.map((row, ri) =>
          createElement(
            'tr',
            { key: ri, className: ri % 2 ? 'bg-slate-50/50 dark:bg-slate-800/20' : '' },
            row.map((cell, ci) =>
              createElement(
                'td',
                { key: ci, className: 'border-b border-slate-100 px-3 py-1.5 align-top dark:border-slate-800' },
                renderInlineText(cell, `td-${ri}-${ci}`),
              ),
            ),
          ),
        ),
      ),
    ),
  )
}

/**
 * Повний рендер документа.
 * @param {string} source  Markdown + LaTeX
 * @param {object} [opts]
 * @param {(text: string) => React.ReactNode} [opts.renderText]
 *   Кастомний рендер для звичайного тексту — сюди підставляється підсвітка наголосів.
 */
export function renderMarkdown(source, opts = {}) {
  const { renderText } = opts
  const text = String(source ?? '').replaceAll('\r\n', '\n')
  if (!text.trim()) return null

  const lines = text.split('\n')
  const blocks = []
  let key = 0

  /** Звичайний абзац із підтримкою переносів рядка (soft wrap → <br/>). */
  const paragraph = (chunk) => {
    const inline = renderInlineText(chunk, `p${key}`)
    if (!renderText) return createElement('p', { key: `b${key++}`, className: 'my-2 leading-relaxed' }, inline)
    // Кастомний рендер: обробляємо кожен «момент» окремо, зберігаючи решту як є
    return createElement('p', { key: `b${key++}`, className: 'my-2 leading-relaxed' }, mapWithRenderer(inline, renderText))
  }

  let i = 0
  while (i < lines.length) {
    const line = lines[i]

    // порожнє — пропускаємо (але зберігаємо відступи між абзацами)
    if (!line.trim()) {
      i++
      continue
    }

    // --- окремий блок формули: $$ ... $$ на кількох рядках або \[ ... \]
    const blockMath = /^\s*\$\$(.*)$/.exec(line)
    if (blockMath) {
      const buffer = [blockMath[1]]
      i++
      let closed = blockMath[1].includes('$$')
      while (i < lines.length && !closed) {
        closed = lines[i].includes('$$')
        buffer.push(lines[i])
        i++
      }
      const joined = buffer.join('\n').replaceAll('$$', '').trim()
      if (joined) blocks.push(renderLatex(joined, true))
      continue
    }

    const bracketMath = /^\s*\\\[(.*)$/.exec(line)
    if (bracketMath) {
      const buffer = [bracketMath[1]]
      i++
      while (i < lines.length && !lines[i].includes('\\]')) {
        buffer.push(lines[i])
        i++
      }
      buffer.push(lines[i] ?? '')
      i++
      const joined = buffer.join('\n').replaceAll('\\[', '').replaceAll('\\]', '').trim()
      if (joined) blocks.push(renderLatex(joined, true))
      continue
    }

    // --- горизонтальна лінія
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push(createElement('hr', { key: `hr${key++}`, className: 'my-4 border-slate-200 dark:border-slate-700' }))
      i++
      continue
    }

    // --- таблиця
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const rows = [splitRow(line)]
      i += 2
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]))
        i++
      }
      blocks.push(renderTable(rows, `tbl${key++}`))
      continue
    }

    // --- заголовки
    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading) {
      const level = heading[1].length
      const sizes = [
        'text-2xl font-bold mt-6 mb-2',
        'text-xl font-bold mt-5 mb-2',
        'text-lg font-semibold mt-4 mb-1.5',
        'text-base font-semibold mt-3 mb-1',
        'text-sm font-semibold mt-3 mb-1',
        'text-sm font-semibold mt-2 mb-1',
      ]
      const Tag = `h${Math.min(level, 6)}`
      blocks.push(
        createElement(Tag, { key: `h${key++}`, className: sizes[level - 1] },
          mapWithRenderer(renderInlineText(heading[2], `h${key}`), renderText)),
      )
      i++
      continue
    }

    // --- цитата
    if (/^\s*>\s?/.test(line)) {
      const buffer = []
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        buffer.push(lines[i].replace(/^\s*>\s?/, ''))
        i++
      }
      blocks.push(
        createElement(
          'blockquote',
          { key: `q${key++}`, className: 'my-3 border-l-4 border-sky-400 bg-sky-50/60 py-2 pl-4 pr-3 text-slate-700 dark:bg-sky-950/25 dark:text-slate-200' },
          mapWithRenderer(renderInlineText(buffer.join(' '), `q${key}`), renderText),
        ),
      )
      continue
    }

    // --- списки
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line)
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    if (bullet || numbered) {
      const ordered = Boolean(numbered)
      const items = []
      while (i < lines.length) {
        const m = ordered
          ? /^\s*\d+[.)]\s+(.*)$/.exec(lines[i])
          : /^\s*[-*+]\s+(.*)$/.exec(lines[i])
        if (!m) break
        items.push(
          createElement('li', { key: `li${i}`, className: 'ml-5 leading-relaxed' },
            mapWithRenderer(renderInlineText(m[1], `li${i}`), renderText)),
        )
        i++
      }
      blocks.push(
        createElement(
          ordered ? 'ol' : 'ul',
          {
            key: `list${key++}`,
            className: `my-2 space-y-1 pl-1 ${ordered ? 'list-decimal' : 'list-disc'} marker:text-slate-400`,
          },
          items,
        ),
      )
      continue
    }

    // --- абзац (збираємо до порожнього рядка)
    const buffer = []
    while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i])) {
      buffer.push(lines[i])
      i++
    }
    if (buffer.length) blocks.push(paragraph(buffer.join('\n')))
  }

  return createElement(Fragment, null, blocks)
}

/** Рядок, з якого починається новий блок (щоб не «з’їдати» його в абзац). */
function isBlockStart(line) {
  return (
    /^\s*#{1,6}\s/.test(line) ||
    /^\s*>/.test(line) ||
    /^\s*[-*+]\s/.test(line) ||
    /^\s*\d+[.)]\s/.test(line) ||
    /^\s*\$\$/.test(line) ||
    /^\s*\\\[/.test(line) ||
    /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)
  )
}

/**
 * Пропускає вузли через renderText, залишаючи решту (LaTeX, <img>, <a>) як є.
 * Рядки з тегом наголосу перетворюються на React-ноди з підсвіченою буквою.
 */
function mapWithRenderer(nodes, renderText) {
  if (!renderText) return nodes
  return nodes.map((node, i) => {
    if (typeof node === 'string' && node.includes(COMBINING_ACUTE)) {
      return createElement(Fragment, { key: `s${i}` }, renderText(node))
    }
    return node
  })
}

/** Екранування для textarea — не потрібне, React робить це сам. Експорт для тестів. */
export { escapeHtml, tokenizeInline, findInlineMathEnd }
